import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, TrendingUp, FileText } from 'lucide-react';
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar } from 'recharts';
import { RADAR_DIMS, COMPETENCIES, SEMANTIC } from '@/data/landing2Data';

const BLUE = SEMANTIC.blue;

export default function ReportShowcase() {
  const { t } = useTranslation();
  const L = (k) => t(`pages.landing2.report.${k}`);
  return (
    <section id="reportes" className="bg-white border-y border-slate-100" data-testid="report-section">
      <div className="max-w-7xl mx-auto px-6 py-20 lg:py-24">
        <div className="max-w-3xl mb-12">
          <span className="inline-flex items-center gap-2 rounded-full bg-teal-50 border border-teal-100 px-4 py-1.5 text-xs font-semibold text-[#176A70]">
            <FileText className="h-3.5 w-3.5" /> {L('eyebrow')}
          </span>
          <h2 className="mt-5 text-3xl sm:text-4xl font-extrabold tracking-tight">{L('heading')}</h2>
          <p className="mt-4 text-slate-600 md:text-lg">{L('sub')}</p>
        </div>

        <div className="rounded-[2rem] bg-white border border-slate-200 shadow-xl overflow-hidden">
          <div className="px-8 py-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3 bg-[#FDFBF7]">
            <div className="flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-white px-2.5 py-1 rounded" style={{ background: BLUE }}>TalentMap</span>
              <div className="font-bold">{L('candidate')} <span className="text-slate-400 font-medium">· {L('role')}</span></div>
            </div>
            <span className="text-[10px] uppercase tracking-wider font-semibold rounded-full px-3 py-1 bg-amber-100 text-amber-800" data-testid="report-fictional-tag">{L('fictional')}</span>
          </div>

          <div className="p-8 lg:p-10 grid lg:grid-cols-5 gap-10">
            <div className="lg:col-span-2">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-2">{L('dims')}</p>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={RADAR_DIMS.map((d) => ({ ...d, label: L(d.key) }))} outerRadius="72%">
                    <PolarGrid stroke="#E7E5E4" />
                    <PolarAngleAxis dataKey="label" tick={{ fill: '#78716C', fontSize: 11 }} />
                    <Radar dataKey="target" stroke={SEMANTIC.amber} fill={SEMANTIC.amber} fillOpacity={0.1} />
                    <Radar dataKey="score" stroke={BLUE} fill={BLUE} fillOpacity={0.2} strokeWidth={2} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              <div className="flex gap-5 text-xs text-slate-500 justify-center">
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: BLUE }} /> {L('personLegend')}</span>
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: SEMANTIC.amber }} /> {L('targetLegend')}</span>
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="flex items-center gap-5 mb-7">
                <div className="relative h-24 w-24 rounded-full grid place-items-center shrink-0" style={{ background: `conic-gradient(${BLUE} 0 87%, #E7E5E4 87% 100%)` }}>
                  <div className="h-[4.6rem] w-[4.6rem] rounded-full bg-white grid place-items-center text-2xl font-extrabold">87%</div>
                </div>
                <div>
                  <div className="text-sm text-slate-500">{L('compat')}</div>
                  <div className="mt-1.5 text-sm font-semibold text-emerald-700 bg-emerald-50 rounded-full px-3 py-1 inline-flex items-center gap-1.5"><Check className="h-4 w-4" /> {L('recBody')}</div>
                </div>
              </div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-3">{L('comps')}</p>
              <div className="space-y-3">
                {COMPETENCIES.map((c) => (
                  <div key={c.key}>
                    <div className="flex justify-between text-sm mb-1 font-medium"><span>{L(c.key)}</span><span className="tabular-nums text-slate-500">{c.v}</span></div>
                    <div className="h-2 rounded-full bg-slate-100">
                      <div className="h-full rounded-full" style={{ width: `${c.v}%`, background: c.v >= 80 ? SEMANTIC.green : c.v >= 70 ? SEMANTIC.turquoise : SEMANTIC.amber }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-1 space-y-6">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-3">{L('strengths')}</p>
                {['s1', 's2'].map((k) => (
                  <div key={k} className="flex gap-2 text-sm text-slate-700 mb-2"><Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: SEMANTIC.green }} />{L(k)}</div>
                ))}
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-3">{L('gaps')}</p>
                {['g1', 'g2'].map((k) => (
                  <div key={k} className="flex gap-2 text-sm text-slate-700 mb-2"><TrendingUp className="h-4 w-4 mt-0.5 shrink-0" style={{ color: SEMANTIC.amber }} />{L(k)}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
